import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DeleteProductImageGQL } from 'src/gql/generated';
import { UserService } from './user.service';

export type ImageOwnerType = 'pricetar' | 'sedorist';

export const ALLOWED_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
export const MAX_IMAGES_PER_PRODUCT = 10;

export interface UploadImageResponse {
  id: number;
  width: number;
  height: number;
  byte_size: number;
  position: number;
}

export interface UploadProgressEvent {
  progress: number;
  response?: UploadImageResponse;
}

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private readonly endpoint = environment.api.base_url + '/api/images';

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private deleteGQL: DeleteProductImageGQL,
  ) {}

  validateClientSide(file: File): string | null {
    if (!ALLOWED_IMAGE_MIME.includes(file.type)) {
      return `非対応の形式です (jpg / png / webp のみ)。受信: ${file.type || '不明'}`;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      const sizeMb = (file.size / 1024 / 1024).toFixed(1);
      return `ファイルサイズが大きすぎます (上限 20MB)。受信: ${sizeMb}MB`;
    }
    return null;
  }

  upload(ownerType: ImageOwnerType, ownerId: number, file: File): Observable<UploadImageResponse> {
    const validationError = this.validateClientSide(file);
    if (validationError) {
      return throwError(() => new Error(validationError));
    }

    const fd = new FormData();
    fd.append('owner_type', ownerType);
    fd.append('owner_id', String(ownerId));
    fd.append('file', file);

    return this.http
      .post<UploadImageResponse>(this.endpoint, fd, { headers: this.authHeaders() })
      .pipe(catchError((e: HttpErrorResponse) => throwError(() => new Error(this.translateUploadError(e)))));
  }

  uploadWithProgress(ownerType: ImageOwnerType, ownerId: number, file: File): Observable<UploadProgressEvent> {
    const validationError = this.validateClientSide(file);
    if (validationError) {
      return throwError(() => new Error(validationError));
    }

    const fd = new FormData();
    fd.append('owner_type', ownerType);
    fd.append('owner_id', String(ownerId));
    fd.append('file', file);

    return this.http
      .post<UploadImageResponse>(this.endpoint, fd, {
        headers: this.authHeaders(),
        observe: 'events',
        reportProgress: true,
      })
      .pipe(
        map((event: HttpEvent<UploadImageResponse>): UploadProgressEvent => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            return { progress: Math.round((100 * event.loaded) / event.total) };
          }
          if (event.type === HttpEventType.Response) {
            return { progress: 100, response: event.body ?? undefined };
          }
          return { progress: 0 };
        }),
        catchError((e: HttpErrorResponse) => throwError(() => new Error(this.translateUploadError(e)))),
      );
  }

  delete(id: string): Observable<boolean> {
    return this.deleteGQL.mutate({ variables: { id } }).pipe(map((res) => !!res.data?.deleteProductImage));
  }

  private authHeaders(): HttpHeaders {
    const token = this.userService.getUserToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('x-app-authorization', token);
    }
    return headers;
  }

  private translateUploadError(e: HttpErrorResponse): string {
    const status = e?.status;
    const code = e?.error && typeof e.error === 'object' ? (e.error as { code?: string }).code : undefined;
    if (status === 415 || code === 'unsupported_media_type') {
      return '対応していない画像形式です (jpg / png / webp のみ)。';
    }
    if (status === 413 || code === 'payload_too_large') {
      return 'ファイルサイズが大きすぎます (上限 20MB)。';
    }
    if (status === 409 || code === 'image_limit_exceeded') {
      return `1商品あたりの画像枚数の上限 (${MAX_IMAGES_PER_PRODUCT}枚) に達しています。`;
    }
    if (status === 404 || code === 'owner_not_found') {
      return '対象の商品が見つかりません。';
    }
    if (status === 403) {
      return 'この操作を行う権限がありません。';
    }
    if (status === 400 || code === 'invalid_image') {
      return '画像の解析に失敗しました。別のファイルでお試しください。';
    }
    return 'アップロードに失敗しました。時間をおいてお試しください。';
  }
}
